import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { isStaticDocumentHref } from "@/utils/staticDocument";
import { ArrowRight } from "lucide-react";
import { Eyebrow, type Domain } from "@appcaire/ui";
import {
  DeviceMock,
  type DeviceMockScreen,
  type DeviceMockVariant,
} from "./DeviceMock";
import {
  bookDemoCtaLabel,
  bookDemoHref,
  type SiteLocale,
} from "@/config/platformAccess";
import { trackMarketingCta } from "@/lib/marketingCta";

// Shared editorial hero for active marketing pages. Route-owned H1 and intro
// stay unchanged while the presentation follows the light product language.

export type HeroCta = { label: string; href: string; external?: boolean };

type PageHeroProps = {
  eyebrow?: ReactNode;
  /** Id for the H1; when set, the hero section is labelled by it. */
  headingId?: string;
  /**
   * What kind of information the page is about (Brand 2.2). Colours the
   * eyebrow in that domain's ink; without it the eyebrow takes the action
   * colour.
   */
  domain?: Domain;
  title: ReactNode;
  description: string;
  primary?: HeroCta;
  secondary?: HeroCta;
  actions?: ReactNode;
  /**
   * Optional custom right-column visual (e.g. film poster card). When set,
   * `image` / `secondaryImage` are ignored.
   */
  visual?: ReactNode;
  image?: {
    variant: DeviceMockVariant;
    src: string;
    alt: string;
    imageFit?: "contain" | "cover";
    objectPosition?: string;
    cardAspect?: "video" | "square";
    className?: string;
  };
  /**
   * Optional companion device, overlapped on the primary one. Use when a page
   * covers two surfaces at once (office + field) and showing only one would
   * under-represent it. Omit for the usual single-device hero.
   *
   * `screen` is the screenshot's intrinsic pixel size. The companion renders
   * height-fitted into a fixed-height slot, so its screen has to be shaped
   * before the image loads — see `DeviceMock`.
   */
  secondaryImage?: {
    // Pinned to "phone": the companion always renders height-fitted, which only
    // the phone frame implements.
    variant: "phone";
    src: string;
    alt: string;
    screen: DeviceMockScreen;
  };
};

function isExternal(cta: HeroCta) {
  return (
    cta.external ||
    /^https?:/.test(cta.href) ||
    cta.href.startsWith("#") ||
    isStaticDocumentHref(cta.href)
  );
}

/**
 * Every "Boka demo" hero CTA resolves to the one canonical booking page and
 * opens in a new tab, so the visitor keeps the page they were reading.
 *
 * This previously rewrote booking CTAs to the interactive demo, which meant the
 * site advertised "Boka demo" on a dozen pages but never actually rendered a
 * booking CTA anywhere. Now that a booking page exists, they resolve to it.
 */
function normalizeDemoCta(cta: HeroCta): HeroCta {
  const isDemoBookingLabel =
    /^(boka demo|boka genomgång|book a demo|book demo|book a walkthrough)$/i.test(
      cta.label.trim(),
    );

  if (!isDemoBookingLabel) return cta;

  const locale: SiteLocale = cta.href.startsWith("/en/") ? "en" : "sv";
  return {
    ...cta,
    label: bookDemoCtaLabel(locale),
    href: bookDemoHref(locale),
    external: true,
  };
}

export function HeroButton({
  cta,
  primary,
}: {
  cta: HeroCta;
  primary?: boolean;
}) {
  const normalizedCta = normalizeDemoCta(cta);
  const booking =
    /^(boka demo|boka genomgång|book a demo|book demo|book a walkthrough)$/i.test(
      cta.label.trim(),
    );
  const className = primary
    ? "inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-caire-action px-6 py-3 text-sm font-semibold text-caire-on-solid transition hover:bg-caire-action-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-caire-action"
    : "inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-caire-ink/45 bg-transparent px-6 py-3 text-sm font-semibold text-caire-ink transition hover:bg-caire-sand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-caire-action";
  const inner = (
    <>
      {normalizedCta.label}
      {primary && <ArrowRight className="h-4 w-4" />}
    </>
  );
  if (isExternal(normalizedCta)) {
    return (
      <a
        href={normalizedCta.href}
        className={className}
        onClick={() =>
          trackMarketingCta(
            booking ? "hero_booking" : "hero_link",
            normalizedCta.label,
            normalizedCta.href,
          )
        }
        target={normalizedCta.external ? "_blank" : undefined}
        rel={normalizedCta.external ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={normalizedCta.href}
      className={className}
      onClick={() =>
        trackMarketingCta("hero_link", normalizedCta.label, normalizedCta.href)
      }
    >
      {inner}
    </Link>
  );
}

export function PageHero({
  eyebrow,
  headingId,
  domain,
  title,
  description,
  primary,
  secondary,
  actions,
  visual,
  image,
  secondaryImage,
}: PageHeroProps) {
  const deviceVisual =
    !visual && image ? (
      secondaryImage ? (
        // Only the two-device hero gets a positioning wrapper; the
        // single-device case stays a direct grid child so its layout is
        // untouched.
        <div className="relative pb-10 sm:pb-0">
          <DeviceMock
            variant={image.variant}
            src={image.src}
            alt={image.alt}
            imageFit={image.imageFit}
            objectPosition={image.objectPosition}
            cardAspect={image.cardAspect}
            className={image.className}
            eager
          />
          <div className="absolute bottom-0 right-2 h-[150px] sm:-bottom-4 sm:h-[190px] lg:-bottom-6 lg:right-4 lg:h-[230px]">
            <DeviceMock
              variant={secondaryImage.variant}
              src={secondaryImage.src}
              alt={secondaryImage.alt}
              fit="height"
              screen={secondaryImage.screen}
              eager
            />
          </div>
        </div>
      ) : (
        <DeviceMock
          variant={image.variant}
          src={image.src}
          alt={image.alt}
          imageFit={image.imageFit}
          objectPosition={image.objectPosition}
          cardAspect={image.cardAspect}
          className={image.className}
          eager
        />
      )
    ) : null;

  return (
    <section
      aria-labelledby={headingId}
      className="relative isolate overflow-hidden border-b border-caire-hairline bg-caire-page py-16 text-caire-ink sm:py-20 lg:py-24"
    >
      <div
        className={`mx-auto grid max-w-[80rem] items-center gap-10 px-6 lg:gap-14 lg:px-8 ${visual || deviceVisual ? "lg:grid-cols-[0.92fr_1.08fr]" : ""}`}
      >
        <div className="flex flex-col justify-center">
          {eyebrow ? (
            <Eyebrow as="div" tone={domain ?? "action"} className="mb-4">
              {eyebrow}
            </Eyebrow>
          ) : null}
          <h1
            id={headingId}
            className="max-w-2xl text-4xl font-semibold leading-[1.05] tracking-[-.035em] text-caire-ink sm:text-5xl"
          >
            {title}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-caire-ink/85">
            {description}
          </p>
          {actions ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {actions}
            </div>
          ) : primary || secondary ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {primary ? <HeroButton cta={primary} primary /> : null}
              {secondary ? <HeroButton cta={secondary} /> : null}
            </div>
          ) : null}
        </div>
        {visual ?? deviceVisual}
      </div>
    </section>
  );
}

export default PageHero;
